import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faChartBar,
    faChevronLeft,
    faChevronRight,
    faClock,
    faExclamationTriangle,
    faList,
    faListCheck,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
    Button,
    Form,
    Modal,
    Spinner,
    Stack,
    Tab,
    Table,
    Tabs,
} from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../../../layout";
import IconElement from "../../../../components/icon";
import { Permissions } from "../../../../app/enums";
import { usePermissionCheck } from "../../../../app/common/helper/Permissions";
import { ServiceManager } from "../../../../services/ServiceManager";
import type {
    IAttendanceManagementCommandHistoryRow,
    IAttendanceManagementConfiguration,
    IAttendanceManagementWritebackSummaryRow,
    IAttendanceRequestError,
    IAttendanceRequestFieldError,
} from "../../../../models/AttendanceManagement";
import CodeCatalogueV2 from "./code-catalogue-v2";
import CommandHistoryV2 from "./command-history-v2";
import type { ICommandHistoryFilterInput } from "./command-history-v2";
import SessionTimingSchedule from "./session-timing-schedule";
import AttendanceStatusChip from "./attendance-status-chip";
import AttendanceUkDateInput from "./attendance-uk-date-input";
import { toIsoDateFromUkDate, toUkDateFromIsoDate } from "./attendance-utils";
import "../../../../assets/css/attendance-configuration.scss";

type ConfigurationUpdater = (
    configuration: IAttendanceManagementConfiguration,
) => IAttendanceManagementConfiguration;
type AttendanceSection = "timing" | "codes" | "summary" | "commands";

export interface IWritebackSummaryRangeInput {
    fromDate: string;
    toDate: string;
}

interface IRequestPanelState {
    error: IAttendanceRequestError | null;
    isLoading: boolean;
    validationErrors: IAttendanceRequestFieldError[];
}

const attendanceSections = [
    {
        id: "timing",
        group: "Configure",
        label: "Session Timing",
        description: "Set the default schedule and only add year-group overrides where needed.",
        icon: faClock,
    },
    {
        id: "codes",
        group: "Configure",
        label: "Code Catalogue",
        description: "Configure enabled writeback codes and the protected-code overwrite exceptions.",
        icon: faList,
    },
    {
        id: "summary",
        group: "Monitor",
        label: "Writeback Summary",
        description: "Review what has been written to the MIS by time and batch.",
        icon: faChartBar,
    },
    {
        id: "commands",
        group: "Monitor",
        label: "Command History",
        description: "Review student attendance mark attempts and their processing outcomes.",
        icon: faListCheck,
    },
] as const;

function isAttendanceSection(value: string | null): value is AttendanceSection {
    return attendanceSections.some((section) => section.id === value);
}

function getAttendanceSectionFromSearch(search: string): AttendanceSection {
    const section = new URLSearchParams(search).get("section");
    return isAttendanceSection(section) ? section : "timing";
}

function createEmptyPanelState(): IRequestPanelState {
    return {
        error: null,
        isLoading: false,
        validationErrors: [],
    };
}

function cloneConfiguration(
    configuration: IAttendanceManagementConfiguration,
): IAttendanceManagementConfiguration {
    return JSON.parse(JSON.stringify(configuration)) as IAttendanceManagementConfiguration;
}

function toUtcDateInputValue(date: Date): string {
    return date.toISOString().slice(0, 10);
}

function createDefaultOperationalRange(now = new Date()): IWritebackSummaryRangeInput {
    const today = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
    ));
    const start = new Date(today);
    start.setUTCDate(start.getUTCDate() - 30);

    return {
        fromDate: toUtcDateInputValue(start),
        toDate: toUtcDateInputValue(today),
    };
}

function createDefaultHistoryFilters(
    range: IWritebackSummaryRangeInput,
): ICommandHistoryFilterInput {
    return {
        ...range,
        class: "",
        search: "",
        status: "",
    };
}

const writebackSummaryPageSize = 20;
const commandHistoryPageSize = 20;

interface ICommandHistoryPagination {
    page: number;
    pageSize: number;
    totalCount: number | null;
}

function createDefaultHistoryPagination(): ICommandHistoryPagination {
    return {
        page: 1,
        pageSize: commandHistoryPageSize,
        totalCount: null,
    };
}

function toUtcStart(value: string): string {
    return /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? `${value}T00:00:00.000Z`
        : value;
}

function toUtcEndExclusive(value: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }

    const end = new Date(`${value}T00:00:00.000Z`);

    if (Number.isNaN(end.getTime())) {
        return value;
    }

    end.setUTCDate(end.getUTCDate() + 1);
    return end.toISOString();
}

function toOperationalRangeQuery(range: IWritebackSummaryRangeInput) {
    return {
        fromUtc: toUtcStart(range.fromDate),
        toUtc: toUtcEndExclusive(range.toDate),
    };
}

function toHistoryQuery(
    filters: ICommandHistoryFilterInput,
    page = 1,
    pageSize = commandHistoryPageSize,
) {
    return {
        ...toOperationalRangeQuery(filters),
        page,
        pageSize,
        class: filters.class.trim() || undefined,
        search: filters.search.trim() || undefined,
        status: filters.status || undefined,
    };
}

function formatUtcTimestamp(value: string | null): string {
    if (!value) {
        return "Pending";
    }

    const timestamp = new Date(value);
    const time = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
    }).format(timestamp);
    const date = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        timeZone: "UTC",
        year: "numeric",
    }).format(timestamp);

    return `${time} · ${date}`;
}

function formatLiveDate(value: string | null): string {
    if (!value) {
        return "Not configured";
    }

    return toUkDateFromIsoDate(value.slice(0, 10)) || "Not configured";
}

function getValidationErrors(error: IAttendanceRequestError): IAttendanceRequestFieldError[] {
    return [
        ...(error.fieldErrors ?? []),
        ...(error.formErrors ?? []),
    ];
}

function AttendanceSectionNavigation(props: {
    activeSection: AttendanceSection;
    onSelect: (section: AttendanceSection) => void;
}) {
    return (
        <nav aria-label="Attendance policy sections" className="attendance-section-tabs">
            <div aria-label="Attendance policy sections" className="attendance-section-tab-list" role="group">
                {attendanceSections.map((section) => (
                    <button
                        aria-current={props.activeSection === section.id ? "page" : undefined}
                        className={`attendance-section-tab ${props.activeSection === section.id ? "is-active" : ""}`}
                        key={section.id}
                        onClick={() => props.onSelect(section.id)}
                        type="button"
                    >
                        <FontAwesomeIcon className="attendance-section-tab-icon" icon={section.icon} />
                        <span>{section.label}</span>
                    </button>
                ))}
            </div>
        </nav>
    );
}

function RequestPanelFeedback(props: {
    error: IAttendanceRequestError | null;
    validationErrors: IAttendanceRequestFieldError[];
}) {
    useEffect(() => {
        const validationMessages = Array.from(new Set(
            props.validationErrors
                .map((error) => error.message.trim())
                .filter(Boolean),
        ));
        const message = validationMessages.length > 0
            ? validationMessages.join(" ")
            : props.error?.message;

        if (message) {
            ServiceManager.ToastService.showError(message);
        }
    }, [props.error, props.validationErrors]);

    return null;
}

export function WritebackSummaryPanel(props: {
    appliedRange: IWritebackSummaryRangeInput;
    error: IAttendanceRequestError | null;
    isLoading: boolean;
    onApply: (range: IWritebackSummaryRangeInput) => void;
    onRangeChange: (range: IWritebackSummaryRangeInput) => void;
    range: IWritebackSummaryRangeInput;
    summaries: IAttendanceManagementWritebackSummaryRow[];
    validationErrors: IAttendanceRequestFieldError[];
}) {
    const applyTimeoutRef = useRef<number | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [fromDateInput, setFromDateInput] = useState(() => toUkDateFromIsoDate(props.range.fromDate));
    const [toDateInput, setToDateInput] = useState(() => toUkDateFromIsoDate(props.range.toDate));
    const totalPages = Math.max(1, Math.ceil(props.summaries.length / writebackSummaryPageSize));
    const currentPage = Math.min(page, totalPages);
    const firstSummaryIndex = (currentPage - 1) * writebackSummaryPageSize;
    const visibleSummaries = props.summaries.slice(
        firstSummaryIndex,
        firstSummaryIndex + writebackSummaryPageSize,
    );
    const firstVisibleSummary = props.summaries.length === 0 ? 0 : firstSummaryIndex + 1;
    const lastVisibleSummary = Math.min(
        firstSummaryIndex + visibleSummaries.length,
        props.summaries.length,
    );
    const hasPagination = totalPages > 1;

    useEffect(() => {
        return () => window.clearTimeout(applyTimeoutRef.current);
    }, []);

    useEffect(() => {
        setPage(1);
    }, [props.summaries]);

    useEffect(() => {
        setFromDateInput(toUkDateFromIsoDate(props.range.fromDate));
    }, [props.range.fromDate]);

    useEffect(() => {
        setToDateInput(toUkDateFromIsoDate(props.range.toDate));
    }, [props.range.toDate]);

    const updateRange = (updates: Partial<IWritebackSummaryRangeInput>) => {
        const range = { ...props.range, ...updates };
        props.onRangeChange(range);
        window.clearTimeout(applyTimeoutRef.current);
        applyTimeoutRef.current = window.setTimeout(() => props.onApply(range), 300);
    };

    const updateFromDate = (value: string) => {
        setFromDateInput(value);
        const isoDate = toIsoDateFromUkDate(value);

        if (isoDate || value === "") {
            updateRange({ fromDate: isoDate ?? "" });
        }
    };

    const updateToDate = (value: string) => {
        setToDateInput(value);
        const isoDate = toIsoDateFromUkDate(value);

        if (isoDate || value === "") {
            updateRange({ toDate: isoDate ?? "" });
        }
    };

    return (
        <div className="attendance-monitor-panel">
            <RequestPanelFeedback error={props.error} validationErrors={props.validationErrors} />
            <div className="attendance-monitor-toolbar">
                <div className="attendance-monitor-filters">
                    <div className="attendance-field">
                        <Form.Label htmlFor="summary-date-from">Start date</Form.Label>
                        <AttendanceUkDateInput
                            disabled={props.isLoading}
                            id="summary-date-from"
                            label="Writeback summary start date"
                            onChange={updateFromDate}
                            placeholder="DD/MM/YYYY"
                            testId="attendance-summary-from-date"
                            value={fromDateInput}
                        />
                    </div>
                    <div className="attendance-field">
                        <Form.Label htmlFor="summary-date-to">End date</Form.Label>
                        <AttendanceUkDateInput
                            disabled={props.isLoading}
                            id="summary-date-to"
                            label="Writeback summary end date"
                            onChange={updateToDate}
                            placeholder="DD/MM/YYYY"
                            testId="attendance-summary-to-date"
                            value={toDateInput}
                        />
                    </div>
                </div>
            </div>
            {props.isLoading ? (
                <div className="attendance-monitor-empty" role="status">Loading writeback summary.</div>
            ) : (
                <>
                    <Table responsive hover className="attendance-data-table mb-0">
                        <caption className="visually-hidden">
                            {`Attendance writeback summary from ${props.appliedRange.fromDate} through ${props.appliedRange.toDate}.`}
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col">Code</th>
                                <th scope="col">Written</th>
                                <th scope="col">Session</th>
                                <th scope="col">Attempted</th>
                                <th scope="col">Successful</th>
                                <th scope="col">Failed</th>
                                <th scope="col">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleSummaries.map((summary) => (
                                <tr key={`${summary.code}-${summary.written}-${summary.session}`}>
                                    <td>{summary.code}</td>
                                    <td>{formatUtcTimestamp(summary.written)}</td>
                                    <td>{summary.session}</td>
                                    <td>{summary.attempted}</td>
                                    <td>{summary.successful}</td>
                                    <td>{summary.failed}</td>
                                    <td><AttendanceStatusChip status={summary.result} /></td>
                                </tr>
                            ))}
                            {props.summaries.length === 0 && (
                                <tr><td className="attendance-monitor-empty" colSpan={7}>No writeback summary is available for the selected range.</td></tr>
                            )}
                        </tbody>
                    </Table>
                    {hasPagination && (
                        <nav aria-label="Writeback summary pagination" className="attendance-writeback-summary-pagination">
                            <span aria-live="polite" className="attendance-writeback-summary-pagination-summary">
                                {`Showing ${firstVisibleSummary}-${lastVisibleSummary} of ${props.summaries.length}`}
                            </span>
                            <div className="attendance-writeback-summary-pagination-actions">
                                <Button
                                    aria-label="Previous writeback summary page"
                                    className="attendance-monitor-pagination-button"
                                    data-testid="attendance-writeback-summary-previous-page"
                                    disabled={currentPage <= 1}
                                    onClick={() => setPage(currentPage - 1)}
                                    size="sm"
                                    title="Previous page"
                                    type="button"
                                    variant="outline-secondary"
                                >
                                    <FontAwesomeIcon aria-hidden="true" icon={faChevronLeft} />
                                </Button>
                                <span className="attendance-writeback-summary-pagination-page">
                                    {`Page ${currentPage} of ${totalPages}`}
                                </span>
                                <Button
                                    aria-label="Next writeback summary page"
                                    className="attendance-monitor-pagination-button"
                                    data-testid="attendance-writeback-summary-next-page"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setPage(currentPage + 1)}
                                    size="sm"
                                    title="Next page"
                                    type="button"
                                    variant="outline-secondary"
                                >
                                    <FontAwesomeIcon aria-hidden="true" icon={faChevronRight} />
                                </Button>
                            </div>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}

export default function AttendanceConfiguration() {
    const { establishmentId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const canEditEstablishments = usePermissionCheck(Permissions.CanEditEstablishments);
    const mountedRef = useRef(true);
    const establishmentIdRef = useRef(establishmentId);
    establishmentIdRef.current = establishmentId;

    const [configuration, setConfiguration] = useState<IAttendanceManagementConfiguration | null>(null);
    const [configurationError, setConfigurationError] = useState<IAttendanceRequestError | null>(null);
    const [draft, setDraft] = useState<IAttendanceManagementConfiguration | null>(null);
    const [isConfigurationLoading, setIsConfigurationLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<IAttendanceRequestError | null>(null);
    const [saveValidationErrors, setSaveValidationErrors] = useState<IAttendanceRequestFieldError[]>([]);
    const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
    const [showOperationalBanner, setShowOperationalBanner] = useState(true);
    const [activeSection, setActiveSection] = useState<AttendanceSection>(() => (
        getAttendanceSectionFromSearch(location.search)
    ));
    const [summaryRange, setSummaryRange] = useState<IWritebackSummaryRangeInput>(() => createDefaultOperationalRange());
    const [appliedSummaryRange, setAppliedSummaryRange] = useState<IWritebackSummaryRangeInput>(() => createDefaultOperationalRange());
    const [writebackSummaries, setWritebackSummaries] = useState<IAttendanceManagementWritebackSummaryRow[]>([]);
    const [summaryState, setSummaryState] = useState<IRequestPanelState>(createEmptyPanelState);
    const [historyFilters, setHistoryFilters] = useState<ICommandHistoryFilterInput>(() => (
        createDefaultHistoryFilters(createDefaultOperationalRange())
    ));
    const [appliedHistoryFilters, setAppliedHistoryFilters] = useState<ICommandHistoryFilterInput>(() => (
        createDefaultHistoryFilters(createDefaultOperationalRange())
    ));
    const [commandHistory, setCommandHistory] = useState<IAttendanceManagementCommandHistoryRow[]>([]);
    const [historyPagination, setHistoryPagination] = useState<ICommandHistoryPagination>(
        createDefaultHistoryPagination,
    );
    const [historyState, setHistoryState] = useState<IRequestPanelState>(createEmptyPanelState);

    useEffect(() => {
        if (configurationError) {
            ServiceManager.ToastService.showError(configurationError.message);
            getValidationErrors(configurationError).forEach((error) => {
                ServiceManager.ToastService.showError(error.message);
            });
        }
    }, [configurationError]);

    useEffect(() => {
        setActiveSection(getAttendanceSectionFromSearch(location.search));
    }, [location.search]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const isActiveEstablishment = (requestedEstablishmentId: string) => (
        mountedRef.current && establishmentIdRef.current === requestedEstablishmentId
    );

    useEffect(() => {
        let isCurrent = true;
        const requestIsCurrent = (requestedEstablishmentId: string) => (
            isCurrent && isActiveEstablishment(requestedEstablishmentId)
        );
        const defaultRange = createDefaultOperationalRange();
        const defaultHistoryFilters = createDefaultHistoryFilters(defaultRange);

        setConfiguration(null);
        setConfigurationError(null);
        setDraft(null);
        setIsEditing(false);
        setIsSaving(false);
        setSaveError(null);
        setSaveValidationErrors([]);
        setShowSaveConfirmation(false);
        setSummaryRange(defaultRange);
        setAppliedSummaryRange(defaultRange);
        setWritebackSummaries([]);
        setSummaryState(createEmptyPanelState());
        setHistoryFilters(defaultHistoryFilters);
        setAppliedHistoryFilters(defaultHistoryFilters);
        setCommandHistory([]);
        setHistoryPagination(createDefaultHistoryPagination());
        setHistoryState(createEmptyPanelState());

        if (!establishmentId) {
            setConfigurationError({
                category: "validation",
                message: "An establishment is required to view attendance policy.",
                retryable: false,
            });
            setIsConfigurationLoading(false);
            return () => {
                isCurrent = false;
            };
        }

        setIsConfigurationLoading(true);

        const loadConfiguration = async () => {
            const result = await ServiceManager.AttendanceManagementService.GetConfiguration(establishmentId);

            if (!requestIsCurrent(establishmentId)) {
                return;
            }

            if (result.ok) {
                setConfiguration(result.value);
                setConfigurationError(null);
            } else {
                setConfiguration(null);
                setConfigurationError(result.error);
            }

            setIsConfigurationLoading(false);
        };

        const loadSummary = async () => {
            setSummaryState({ error: null, isLoading: true, validationErrors: [] });
            const result = await ServiceManager.AttendanceManagementService.GetWritebackSummary(
                establishmentId,
                toOperationalRangeQuery(defaultRange),
            );

            if (!requestIsCurrent(establishmentId)) {
                return;
            }

            if (result.ok) {
                setWritebackSummaries(result.value);
                setAppliedSummaryRange(defaultRange);
                setSummaryState(createEmptyPanelState());
            } else {
                setWritebackSummaries([]);
                setSummaryState({
                    error: result.error,
                    isLoading: false,
                    validationErrors: getValidationErrors(result.error),
                });
            }
        };

        const loadHistory = async () => {
            const defaultHistoryPagination = createDefaultHistoryPagination();
            setHistoryState({ error: null, isLoading: true, validationErrors: [] });
            const result = await ServiceManager.AttendanceManagementService.GetCommandHistory(
                establishmentId,
                toHistoryQuery(
                    defaultHistoryFilters,
                    defaultHistoryPagination.page,
                    defaultHistoryPagination.pageSize,
                ),
            );

            if (!requestIsCurrent(establishmentId)) {
                return;
            }

            if (result.ok) {
                setCommandHistory(result.value.rows);
                setHistoryPagination({
                    page: result.value.page ?? defaultHistoryPagination.page,
                    pageSize: result.value.pageSize ?? defaultHistoryPagination.pageSize,
                    totalCount: result.value.totalCount ?? null,
                });
                setAppliedHistoryFilters(defaultHistoryFilters);
                setHistoryState(createEmptyPanelState());
            } else {
                setCommandHistory([]);
                setHistoryPagination(createDefaultHistoryPagination());
                setHistoryState({
                    error: result.error,
                    isLoading: false,
                    validationErrors: getValidationErrors(result.error),
                });
            }
        };

        void loadConfiguration();
        void loadSummary();
        void loadHistory();

        return () => {
            isCurrent = false;
        };
    }, [establishmentId]);

    const updateDraft = (updater: ConfigurationUpdater) => {
        setDraft((currentDraft) => currentDraft ? updater(currentDraft) : currentDraft);
    };

    const selectSection = (section: AttendanceSection) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("section", section);
        setActiveSection(section);
        navigate({
            hash: location.hash,
            pathname: location.pathname,
            search: `?${searchParams.toString()}`,
        }, { replace: true });
    };

    const startEditing = () => {
        if (!configuration) {
            return;
        }

        setDraft(cloneConfiguration(configuration));
        setSaveError(null);
        setSaveValidationErrors([]);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setDraft(null);
        setSaveError(null);
        setSaveValidationErrors([]);
        setShowSaveConfirmation(false);
        setIsEditing(false);
    };

    const requestSave = () => {
        if (draft) {
            setSaveError(null);
            setSaveValidationErrors([]);
            setShowSaveConfirmation(true);
        }
    };

    const saveConfiguration = async () => {
        if (!draft || !establishmentId) {
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        setSaveValidationErrors([]);
        const result = await ServiceManager.AttendanceManagementService.SaveConfiguration(establishmentId, draft);

        if (!isActiveEstablishment(establishmentId)) {
            return;
        }

        if (result.ok) {
            setConfiguration(result.value);
            setDraft(null);
            setShowSaveConfirmation(false);
            setIsEditing(false);
            ServiceManager.ToastService.showSuccess("Attendance policy saved.");
        } else {
            setSaveError(result.error);
            setSaveValidationErrors(getValidationErrors(result.error));
            setShowSaveConfirmation(false);
        }

        setIsSaving(false);
    };

    const requestSummary = async (range: IWritebackSummaryRangeInput) => {
        if (!establishmentId) {
            return;
        }

        setSummaryState({ error: null, isLoading: true, validationErrors: [] });
        const result = await ServiceManager.AttendanceManagementService.GetWritebackSummary(
            establishmentId,
            toOperationalRangeQuery(range),
        );

        if (!isActiveEstablishment(establishmentId)) {
            return;
        }

        if (result.ok) {
            setWritebackSummaries(result.value);
            setAppliedSummaryRange(range);
            setSummaryState(createEmptyPanelState());
        } else {
            setWritebackSummaries([]);
            setSummaryState({
                error: result.error,
                isLoading: false,
                validationErrors: getValidationErrors(result.error),
            });
        }
    };

    const requestHistory = async (
        filters: ICommandHistoryFilterInput,
        page = 1,
        pageSize = commandHistoryPageSize,
    ) => {
        if (!establishmentId) {
            return;
        }

        setHistoryState({ error: null, isLoading: true, validationErrors: [] });
        const result = await ServiceManager.AttendanceManagementService.GetCommandHistory(
            establishmentId,
            toHistoryQuery(filters, page, pageSize),
        );

        if (!isActiveEstablishment(establishmentId)) {
            return;
        }

        if (result.ok) {
            setCommandHistory(result.value.rows);
            setHistoryPagination({
                page: result.value.page ?? page,
                pageSize: result.value.pageSize ?? pageSize,
                totalCount: result.value.totalCount ?? null,
            });
            setAppliedHistoryFilters(filters);
            setHistoryState(createEmptyPanelState());
        } else {
            setCommandHistory([]);
            setHistoryPagination(createDefaultHistoryPagination());
            setHistoryState({
                error: result.error,
                isLoading: false,
                validationErrors: getValidationErrors(result.error),
            });
        }
    };

    const hasUnsavedChanges = isEditing
        && draft !== null
        && configuration !== null
        && JSON.stringify(draft) !== JSON.stringify(configuration);

    useEffect(() => {
        if (!hasUnsavedChanges) {
            return undefined;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const visibleConfiguration = isEditing ? draft : configuration;
    const isWritebackStatusUnavailable = isConfigurationLoading
        || configurationError !== null
        || configuration === null
        || configuration.writebackEnabled === null;
    const bannerState = isWritebackStatusUnavailable
        ? "attention"
        : configuration.writebackEnabled
            ? "ready"
            : "paused";
    const writebackLabel = isConfigurationLoading
        ? "Checking writeback status"
        : isWritebackStatusUnavailable
            ? "Writeback status unavailable"
            : configuration.writebackEnabled
                ? "Writeback enabled"
                : "Writeback disabled";
    const liveDateLabel = isConfigurationLoading
        ? "Checking LiveDate"
        : configurationError || !configuration
            ? "LiveDate unavailable"
            : formatLiveDate(configuration.liveDate);
    const activeSectionDefinition = attendanceSections.find((section) => section.id === activeSection) ?? attendanceSections[0];

    return (
        <Layout
            title="Attendance Policy"
            backArrow={<FontAwesomeIcon className="me-4 fs-5 cursor-pointer" icon={faArrowLeft} onClick={() => navigate(-1)} />}
            button={
                canEditEstablishments && configuration ? (
                    isEditing ? (
                        <Stack direction="horizontal" gap={2}>
                            <Button data-testid="attendance-policy-cancel" disabled={isSaving} onClick={cancelEditing} variant="outline-secondary">
                                Cancel
                            </Button>
                            <Button data-testid="attendance-policy-save" disabled={isSaving} onClick={requestSave} variant="primary">
                                {isSaving ? "Saving" : "Save Changes"}
                            </Button>
                        </Stack>
                    ) : (
                        <Button data-testid="attendance-policy-edit" onClick={startEditing} variant="primary">Edit Configuration</Button>
                    )
                ) : undefined
            }
        >
            <div className="attendance-workspace">
                {showOperationalBanner && (
                    <section aria-label="Attendance writeback status" className={`attendance-operational-banner is-${bannerState}`}>
                        <span className="attendance-banner-item attendance-banner-primary">
                            <span aria-hidden="true" className="attendance-banner-status-dot" />
                            <strong>{writebackLabel}</strong>
                        </span>
                        <div className="attendance-banner-facts">
                            <span className="attendance-banner-item">
                                Managed in <strong className="attendance-banner-value">MIS Client</strong>
                            </span>
                            <span className="attendance-banner-item">
                                LiveDate <strong className="attendance-banner-value">{liveDateLabel}</strong>
                            </span>
                            {!canEditEstablishments && <span className="attendance-view-only">View-only access</span>}
                        </div>
                        <button
                            aria-label="Close attendance writeback status"
                            className="attendance-banner-dismiss"
                            onClick={() => setShowOperationalBanner(false)}
                            title="Close status banner"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </section>
                )}

                <RequestPanelFeedback error={saveError} validationErrors={saveValidationErrors} />

                <div className="attendance-workspace-layout">
                    <AttendanceSectionNavigation activeSection={activeSection} onSelect={selectSection} />
                    <section aria-labelledby="attendance-section-heading" className="attendance-content-area">
                        {activeSection !== "timing" && activeSection !== "codes" && (
                            <header className={`attendance-content-header${activeSection === "summary" ? " is-writeback-summary" : ""}${activeSection === "commands" ? " is-command-history" : ""}`}>
                                <div className="attendance-content-header-copy">
                                    <h2 id="attendance-section-heading">{activeSectionDefinition.label}</h2>
                                    <p>{activeSectionDefinition.description}</p>
                                </div>
                            </header>
                        )}
                        <Tabs
                            activeKey={activeSection}
                            className="attendance-hidden-tabs"
                            mountOnEnter
                            onSelect={(section) => {
                                if (section) {
                                    selectSection(section as AttendanceSection);
                                }
                            }}
                        >
                            <Tab eventKey="timing" title="Session Timing">
                                {isConfigurationLoading && (
                                    <div className="d-flex justify-content-center py-5"><Spinner animation="border" role="status" /></div>
                                )}
                                {!isConfigurationLoading && visibleConfiguration && (
                                    <SessionTimingSchedule
                                        configuration={visibleConfiguration}
                                        isEditing={isEditing}
                                        onChange={(nextConfiguration) => updateDraft(() => nextConfiguration)}
                                    />
                                )}
                            </Tab>
                            <Tab eventKey="codes" title="Code Catalogue">
                                {isConfigurationLoading && (
                                    <div className="d-flex justify-content-center py-5"><Spinner animation="border" role="status" /></div>
                                )}
                                {!isConfigurationLoading && visibleConfiguration && (
                                    <CodeCatalogueV2
                                        configuration={visibleConfiguration}
                                        isEditing={isEditing}
                                        onChange={(nextConfiguration) => updateDraft(() => nextConfiguration)}
                                    />
                                )}
                            </Tab>
                            <Tab eventKey="summary" title="Writeback Summary">
                                <WritebackSummaryPanel
                                    appliedRange={appliedSummaryRange}
                                    error={summaryState.error}
                                    isLoading={summaryState.isLoading}
                                    onApply={(range) => void requestSummary(range)}
                                    onRangeChange={setSummaryRange}
                                    range={summaryRange}
                                    summaries={writebackSummaries}
                                    validationErrors={summaryState.validationErrors}
                                />
                            </Tab>
                            <Tab eventKey="commands" title="Command History">
                                <CommandHistoryV2
                                    appliedFilters={appliedHistoryFilters}
                                    error={historyState.error}
                                    filters={historyFilters}
                                    isLoading={historyState.isLoading}
                                    onApply={(filters) => void requestHistory(filters)}
                                    onFiltersChange={setHistoryFilters}
                                    onPageChange={(page) => void requestHistory(
                                        appliedHistoryFilters,
                                        page,
                                        historyPagination.pageSize,
                                    )}
                                    page={historyPagination.page}
                                    pageSize={historyPagination.pageSize}
                                    rows={commandHistory}
                                    totalCount={historyPagination.totalCount}
                                    validationErrors={historyState.validationErrors}
                                />
                            </Tab>
                        </Tabs>
                    </section>
                </div>
            </div>

            <Modal
                backdrop="static"
                centered
                contentClassName="attendance-save-confirmation-modal"
                dialogClassName="attendance-save-confirmation-dialog"
                keyboard={false}
                onHide={() => {
                    if (!isSaving) {
                        setShowSaveConfirmation(false);
                    }
                }}
                show={showSaveConfirmation}
            >
                <Modal.Header closeButton={!isSaving}>
                    <Modal.Title className="attendance-save-confirmation-title">
                        <IconElement color="#F79009" headerClass="warning-icon" iconType={faExclamationTriangle} />
                        <span>Confirm attendance policy changes</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="attendance-save-confirmation-copy">
                        These changes may affect live attendance writeback.
                    </p>
                    <p className="attendance-save-confirmation-audit-empty">
                        Are you sure you want to save these changes?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button data-testid="attendance-policy-confirm-cancel" disabled={isSaving} onClick={() => setShowSaveConfirmation(false)} variant="outline-secondary">
                        Cancel
                    </Button>
                    <Button data-testid="attendance-policy-confirm-save" disabled={isSaving} onClick={saveConfiguration} variant="primary">
                        {isSaving ? "Saving" : "Confirm and Save"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Layout>
    );
}